// src/pages/Dashboard/Admin/AdherentPret/hooks/useAdherentPretDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllAdherentPrets,
  createAdherentPret,
  updateAdherentPret,
  deleteAdherentPret,
} from "../../../../../services/adherentpretServices";

import { getAllAdherentsList } from "../../../../../services/adherentServices";
import { getArticles } from "../../../../../services/articlesServices";

import {
  buildCreatePretPayload,
  buildUpdatePretPayload,
  calculatePretStats,
  filterPrets,
  isPretArticle,
  sortAdherents,
  sortArticles,
} from "../utils/adherentPretDashboardUtils";

const defaultFilters = {
  query: "",
  status: "",
  sort: "date_desc",
};

export function useAdherentPretDashboard() {
  const [prets, setPrets] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [articlesPret, setArticlesPret] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [currentPret, setCurrentPret] = useState(null);

  const fetchPrets = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getAllAdherentPrets();
      setPrets(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur chargement prêts :", error);
      toast.error("Erreur lors du chargement des prêts.");
      setPrets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      setLoadingMeta(true);

      const [adherentsResponse, articlesResponse] = await Promise.all([
        getAllAdherentsList(),
        getArticles(),
      ]);

      const adherentsRows = Array.isArray(adherentsResponse)
        ? adherentsResponse
        : [];

      const articleRows = Array.isArray(articlesResponse)
        ? articlesResponse
        : [];

      setAdherents(sortAdherents(adherentsRows));
      setArticlesPret(sortArticles(articleRows.filter(isPretArticle)));
    } catch (error) {
      console.error("Erreur chargement listes prêt :", error);
      toast.error("Erreur lors du chargement des adhérents ou du matériel.");
      setAdherents([]);
      setArticlesPret([]);
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    fetchPrets();
    fetchMeta();
  }, [fetchPrets, fetchMeta]);

  const filteredPrets = useMemo(() => {
    return filterPrets(prets, filters);
  }, [prets, filters]);

  const stats = useMemo(() => {
    return calculatePretStats(prets, filteredPrets);
  }, [prets, filteredPrets]);

  const openAddDrawer = () => {
    setAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    if (saving) return;
    setAddDrawerOpen(false);
  };

  const openEditDrawer = (pret) => {
    setCurrentPret(pret);
    setEditDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    if (saving) return;
    setCurrentPret(null);
    setEditDrawerOpen(false);
  };

  const handleCreatePret = async (formData) => {
    try {
      setSaving(true);

      const payload = buildCreatePretPayload(formData);
      await createAdherentPret(payload);

      toast.success("Prêt enregistré avec succès.");
      setAddDrawerOpen(false);

      await fetchPrets();
    } catch (error) {
      console.error("Erreur création prêt :", error);
      toast.error("Erreur lors de la création du prêt.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePret = async (pretId, formData) => {
    if (!pretId) {
      toast.error("Prêt introuvable.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildUpdatePretPayload(formData);
      await updateAdherentPret(pretId, payload);

      toast.success("Prêt mis à jour avec succès.");
      setEditDrawerOpen(false);
      setCurrentPret(null);

      await fetchPrets();
    } catch (error) {
      console.error("Erreur mise à jour prêt :", error);
      toast.error("Erreur lors de la mise à jour du prêt.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePret = async (pretOrId) => {
    const id = typeof pretOrId === "object" ? pretOrId?.id : pretOrId;

    if (!id) {
      toast.error("Prêt introuvable.");
      return;
    }

    if (!window.confirm("Supprimer ce prêt de matériel ?")) {
      return;
    }

    try {
      setDeletingId(id);

      await deleteAdherentPret(id);

      toast.success("Prêt supprimé avec succès.");

      setPrets((prev) => prev.filter((pret) => pret.id !== id));

      if (currentPret?.id === id) {
        setCurrentPret(null);
        setEditDrawerOpen(false);
      }
    } catch (error) {
      console.error("Erreur suppression prêt :", error);
      toast.error("Erreur lors de la suppression du prêt.");
    } finally {
      setDeletingId(null);
    }
  };

  const refreshPrets = useCallback(async () => {
    await fetchPrets();
  }, [fetchPrets]);

  return {
    prets,
    filteredPrets,
    adherents,
    articlesPret,
    stats,

    filters,
    setFilters,

    loading,
    loadingMeta,
    saving,
    deletingId,

    addDrawerOpen,
    editDrawerOpen,
    currentPret,

    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,

    handleCreatePret,
    handleUpdatePret,
    handleDeletePret,
    refreshPrets,
  };
}